import { useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import * as yup from 'yup'
import { Button, NumberInput, Select, Switch, TextInput } from '@mantine/core'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import RichTextField from '../../components/RichTextField/RichTextField'
import {
  createOrUpdateAutomation,
  getAutomation,
  toggleAutomation,
  deleteAutomation,
  sendTestAutomationEmail,
} from './helper'

type Props = RouteComponentProps<{ id?: string }>

interface IFormProps {
  id?: number
  name: string
  targetStatus: string
  triggerType: string
  triggerDays: number | ''
  subject: string
  contentHtml: string
  repeatSend: boolean
}

const TARGET_STATUS_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed coaches (active + expiring soon)' },
  { value: 'active', label: 'Active only' },
  { value: 'expiringSoon', label: 'Expiring soon' },
  { value: 'expired', label: 'Expired' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
]

const TRIGGER_TYPE_OPTIONS = [
  { value: 'expires_in_days', label: 'Subscription expires in N days' },
  { value: 'expired_after_days', label: 'N days after subscription expired' },
  { value: 'unsubscribed_after_days', label: 'N days after becoming unsubscribed' },
  { value: 'subscribed_after_days', label: 'N days after becoming subscribed' },
  { value: 'recurring_every_minute', label: 'Every minute (testing)' },
  { value: 'recurring_daily', label: 'Every day' },
  { value: 'recurring_weekly', label: 'Every week' },
  { value: 'recurring_monthly', label: 'Every month' },
]

const DAY_COUNT_TRIGGERS = ['expires_in_days', 'expired_after_days', 'unsubscribed_after_days', 'subscribed_after_days']

const PLACEHOLDERS = [
  { token: '{{coach_name}}', label: 'Coach name' },
  { token: '{{email}}', label: 'Email' },
  { token: '{{subscription_plan}}', label: 'Plan' },
  { token: '{{subscription_expiry_date}}', label: 'Expiry date' },
  { token: '{{subscription_start_date}}', label: 'Start date (not always available)' },
]

export default function AutomationFormPage({ match, history }: Props) {
  const [automation, setAutomation] = useState<any>()
  const [loadError, setLoadError] = useState<string | null>(null)
  const [statusBusy, setStatusBusy] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const isNew = !match.params.id || match.params.id === 'new'

  const reload = () =>
    getAutomation(match.params.id!)
      .then((res) => setAutomation(res.data))
      .catch((err) => setLoadError(err || 'Failed to load automation'))

  useEffect(() => {
    if (!isNew) {
      reload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.params.id, isNew])

  const onToggleStatus = () => {
    if (!automation) return
    const next = automation.status === 'enabled' ? 'disabled' : 'enabled'
    setStatusBusy(true)
    toggleAutomation(automation.id, next)
      .then(reload)
      .catch((err) => window.alert(err || 'Action failed'))
      .finally(() => setStatusBusy(false))
  }

  const onDelete = () => {
    if (!automation) return
    if (!window.confirm(`Delete automation "${automation.name}"? This also deletes its send log.`)) return
    setDeleting(true)
    deleteAutomation(automation.id)
      .then(() => history.push('/subscriptions/automation'))
      .catch((err) => {
        window.alert(err || 'Delete failed')
        setDeleting(false)
      })
  }

  const [testEmail, setTestEmail] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)

  const onSendTest = (subject: string, contentHtml: string) => {
    if (!testEmail) {
      setTestResult({ ok: false, message: 'Enter an email address first.' })
      return
    }
    setTestSending(true)
    setTestResult(null)
    sendTestAutomationEmail(testEmail, subject, contentHtml)
      .then(() => setTestResult({ ok: true, message: `Test email sent to ${testEmail}.` }))
      .catch((err) => setTestResult({ ok: false, message: err || 'Failed to send test email.' }))
      .finally(() => setTestSending(false))
  }

  const initialValues: IFormProps = {
    id: automation?.id,
    name: automation?.name || '',
    targetStatus: automation?.targetStatus || 'subscribed',
    triggerType: automation?.triggerType || 'expires_in_days',
    triggerDays: automation?.triggerDays ?? 7,
    subject: automation?.subject || '',
    contentHtml: automation?.contentHtml || '',
    repeatSend: automation?.repeatSend || false,
  }

  const [submitError, setSubmitError] = useState<string | null>(null)

  const onSubmit = (values: IFormProps, formikHelpers: FormikHelpers<IFormProps>) => {
    setSubmitError(null)
    createOrUpdateAutomation(values)
      .then(() => history.push('/subscriptions/automation'))
      .catch((err) => setSubmitError(err || 'Failed to save automation'))
      .finally(() => formikHelpers.setSubmitting(false))
  }

  const validationSchema = yup.object({
    name: yup.string().required(),
    targetStatus: yup.string().required(),
    triggerType: yup.string().required(),
    triggerDays: yup.number().when('triggerType', {
      is: (v: string) => DAY_COUNT_TRIGGERS.includes(v),
      then: yup.number().min(0).required(),
    }),
    subject: yup.string().required(),
    contentHtml: yup.string().required(),
  })

  return (
    <DashboardWrapper page="subscriptions-automation" title={isNew ? 'Create Automation' : 'Edit Automation'}>
      {loadError && (
        <div className="bg-white border border-red-200 rounded-xl p-4 text-sm text-red-600 mb-4 max-w-2xl">
          Couldn't load this automation: {loadError}
        </div>
      )}
      {!isNew && automation && (
        <div className="flex items-center gap-3 bg-white rounded-xl shadow-md p-4 mb-4 max-w-2xl">
          <span
            className="text-xs font-bold px-2.5 py-1 rounded-full"
            style={{
              background: automation.status === 'enabled' ? '#EAF7EF' : '#F1F5F9',
              color: automation.status === 'enabled' ? '#16A34A' : '#64748B',
            }}
          >
            {automation.status === 'enabled' ? 'Enabled' : 'Disabled'}
          </span>
          <Button size="xs" variant="outline" onClick={onToggleStatus} disabled={statusBusy}>
            {automation.status === 'enabled' ? 'Disable' : 'Enable'}
          </Button>
          <Button size="xs" color="red" variant="outline" onClick={onDelete} disabled={deleting} style={{ marginLeft: 'auto' }}>
            Delete Automation
          </Button>
        </div>
      )}
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema} enableReinitialize>
        {(formikProps: FormikProps<IFormProps>) => (
          <Form className="flex flex-col gap-5 bg-white rounded-xl shadow-md p-6 max-w-2xl">
            <Field as={TextInput} name="name" label="Automation Name" required />

            <Select
              label="Target coach subscription status"
              data={TARGET_STATUS_OPTIONS}
              value={formikProps.values.targetStatus}
              onChange={(v) => formikProps.setFieldValue('targetStatus', v)}
              required
            />

            <Select
              label="Trigger"
              data={TRIGGER_TYPE_OPTIONS}
              value={formikProps.values.triggerType}
              onChange={(v) => formikProps.setFieldValue('triggerType', v)}
              required
            />

            {DAY_COUNT_TRIGGERS.includes(formikProps.values.triggerType) && (
              <NumberInput
                label="Days"
                min={0}
                value={formikProps.values.triggerDays === '' ? undefined : formikProps.values.triggerDays}
                onChange={(v) => formikProps.setFieldValue('triggerDays', v ?? '')}
                required
              />
            )}

            <Switch
              label="Repeat — keep sending while the condition holds (not just once)"
              checked={formikProps.values.repeatSend}
              onChange={(e) => formikProps.setFieldValue('repeatSend', e.currentTarget.checked)}
            />

            <Field as={TextInput} name="subject" label="Email Subject" required />

            <div>
              <label className="text-sm font-medium block mb-1">Email Content</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p.token}
                    type="button"
                    className="text-xs px-2 py-1 rounded bg-[#F1F5F9] text-[#0B1D3A] hover:bg-[#E2E8F0]"
                    onClick={() =>
                      formikProps.setFieldValue('contentHtml', (formikProps.values.contentHtml || '') + ' ' + p.token)
                    }
                    title={p.label}
                  >
                    + {p.label}
                  </button>
                ))}
              </div>
              <RichTextField
                value={formikProps.values.contentHtml}
                onChange={(html) => formikProps.setFieldValue('contentHtml', html)}
                placeholder="Write the email body — use the buttons above to insert coach details."
              />
            </div>

            <div className="border-t pt-4 flex flex-col gap-2">
              <label className="text-sm font-medium">Send a test email</label>
              <p className="text-xs text-gray-400">
                Uses the subject/content above with sample coach data — doesn't affect the send log or count
                towards any real coach.
              </p>
              <div className="flex gap-2 items-start">
                <TextInput
                  placeholder="you@viveka.world"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={testSending || !formikProps.values.subject || !formikProps.values.contentHtml}
                  onClick={() => onSendTest(formikProps.values.subject, formikProps.values.contentHtml)}
                >
                  {testSending ? 'Sending…' : 'Send Test'}
                </Button>
              </div>
              {testResult && (
                <p className={`text-xs ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>{testResult.message}</p>
              )}
            </div>

            {submitError && <p className="text-xs text-red-600">Couldn't save: {submitError}</p>}

            <Button
              type="submit"
              disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
              style={{ width: 'fit-content' }}
            >
              {isNew ? 'Save (as Disabled)' : 'Save'}
            </Button>
            {isNew && (
              <p className="text-xs text-gray-400">
                New automations save as Disabled — enable it from the automation list once you're happy with it.
              </p>
            )}
          </Form>
        )}
      </Formik>
    </DashboardWrapper>
  )
}
